import React, { useRef, useState } from "react";
import { observer } from "mobx-react";
import { Box } from "@mui/material";
import { InputString } from "../../entities/common/controls";
import { useRootStore } from "../../store/contexts";
import {
  formatIswcForDisplay,
  IdentifierCheck,
} from "../../entities/common/validation/identifiers";
import { messageFor } from "../../entities/common/validation/identifiers/identifierMessages";
import {
  checkIdentifier,
  IdentifierKind,
} from "../../entities/common/validation/identifierValidation";

/**
 * Campo de ISRC, ISWC o IPI, común a los cuatro componentes custom
 * (PhonogramIsrc, MusicalWorkIswc, AuthorComposerIpi, PublisherCaeIpi).
 *
 * - Al salir del campo, si el usuario escribió, guarda la forma canónica
 *   (`AR-F03-97-03368` → `ARF039703368`, `260876058` → `00260876058`) y muestra el
 *   error o el aviso de la regla en el idioma del usuario (messageFor, los mismos
 *   textos que da el server). Solo después de salir: mientras se escribe no hay
 *   mensajes, y entrar y salir sin escribir no toca un dato viejo.
 * - Al volver a escribir, el mensaje local se borra.
 * - El error del store (al guardar) manda sobre el local. Si llega `helperText` sin
 *   `error`, es un aviso del store (`store.warnings`) y se muestra como aviso.
 * - El ISWC se muestra como `T-034.524.680-1` mientras el campo no tiene el foco; se
 *   edita y se guarda en la forma de 11 caracteres.
 *
 * El blur se escucha en un Box que envuelve al InputString (React propaga focus y
 * blur): así no hace falta un onBlur en el control generado. `display: contents` deja
 * el layout del form igual que con el InputString suelto.
 */
export interface IdentifierFieldProps {
  kind: IdentifierKind;
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  description?: string;
  // Campo opcional: el form no le pasa nullText a un customComponent, así que el
  // "(Opcional)" del label lo pone el campo.
  optional?: boolean;
}

export const IdentifierField: React.FC<IdentifierFieldProps> = observer(
  ({ kind, value, onChange, error, helperText, label, description, optional }) => {
    const { langStore } = useRootStore();
    const [focused, setFocused] = useState(false);
    // Texto que tenía el campo al recibir el foco: si al salir es el mismo, el usuario no
    // lo cambió (aunque haya escrito y borrado) y no se normaliza ni se llama a onChange.
    const textAtFocus = useRef<string | null>(null);
    // El chequeo local va atado al texto que chequeó: solo se muestra mientras el valor
    // siga siendo ese (un cambio de afuera o volver a escribir lo borra de la vista).
    const [blurCheck, setBlurCheck] = useState<{ check: IdentifierCheck; text: string } | null>(
      null
    );

    const text: string = typeof value === "string" ? value : value == null ? "" : String(value);

    const handleChange = (next: string) => {
      onChange(next);
    };

    const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
      // El foco se movió dentro del mismo campo (p.ej. al ícono de ayuda): no es salir.
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      setFocused(false);
      const atFocus = textAtFocus.current;
      textAtFocus.current = null;
      if (atFocus === null || atFocus === text) return;
      const check = checkIdentifier(kind, text);
      const normalized = check.value ?? "";
      if (normalized !== text) onChange(normalized);
      setBlurCheck({ check, text: normalized });
    };

    const current = blurCheck && blurCheck.text === text ? blurCheck.check : null;
    const localError = current?.error ? messageFor(current.error, langStore.language) : undefined;
    const localWarning = current?.warning
      ? messageFor(current.warning, langStore.language)
      : undefined;

    let shownError: string | undefined;
    let shownWarning: string | undefined;
    if (error) shownError = helperText ?? "";
    else if (localError) shownError = localError;
    else shownWarning = helperText || localWarning;

    const shownValue = kind === "iswc" && !focused ? formatIswcForDisplay(text) : text;

    return (
      <Box
        sx={{ display: "contents" }}
        onFocus={() => {
          if (textAtFocus.current === null) textAtFocus.current = text;
          setFocused(true);
        }}
        onBlur={handleBlur}
      >
        <InputString
          value={shownValue}
          error={shownError !== undefined}
          warning={shownWarning !== undefined}
          helperText={shownError ?? shownWarning ?? ""}
          label={label || ""}
          description={description}
          nullText={optional ? langStore.optionalFieldHint : undefined}
          onChange={handleChange}
        />
      </Box>
    );
  }
);
